import React, { useEffect, useState } from 'react';
import { AlertTriangle, CreditCard, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subscription grace banner — Phase 1 (Recurring Billing).
 *
 * Reads /api/auth/me to learn the current subscription_state and renders
 * a non-modal banner only when the user is in `past_due`, `read_only`, or
 * `lapsed`. Otherwise renders nothing.
 *
 * The CTA navigates the user to /settings (the existing subscription
 * management surface) where they can update their payment method.
 *
 * Dismissible inside `past_due` only — once the user has dismissed it,
 * we hide it for the current session via sessionStorage so we don't nag.
 * It re-appears the next time they open the app.
 */
const STATE_STYLES = {
  past_due: {
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    icon: 'text-amber-700',
    title: 'text-amber-900',
    body: 'text-amber-800',
    button: 'bg-amber-700 hover:bg-amber-800 text-white',
    cta: 'Update payment method',
    title_text: 'Your portfolio is open — we just need to refresh your payment method',
    dismissible: true,
  },
  read_only: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    icon: 'text-red-700',
    title: 'text-red-900',
    body: 'text-red-800',
    button: 'bg-red-700 hover:bg-red-800 text-white',
    cta: 'Restore full access',
    title_text: 'Your data is intact — full access returns the moment you renew',
    dismissible: false,
  },
  lapsed: {
    bg: 'bg-slate-50',
    border: 'border-slate-300',
    icon: 'text-slate-600',
    title: 'text-slate-900',
    body: 'text-slate-700',
    button: 'bg-slate-800 hover:bg-slate-900 text-white',
    cta: 'Let’s reconnect',
    title_text: 'Your portfolio is preserved — we’d like to welcome you back',
    dismissible: false,
  },
};

const SubscriptionGraceBanner = () => {
  const [state, setState] = useState(null);
  const [hint, setHint] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setState(data.subscription_state || null);
        setHint(data.subscription_banner_hint || null);
        if (sessionStorage.getItem('grace_banner_dismissed') === data.subscription_state) {
          setDismissed(true);
        }
      })
      .catch(() => {});
  }, []);

  if (!state || dismissed) return null;
  const style = STATE_STYLES[state];
  if (!style) return null;

  const handleCta = () => {
    if (state === 'lapsed') {
      window.location.href = 'mailto:ceo@myinvestfolio.io?subject=Restore%20my%20subscription';
    } else {
      window.location.href = '/settings';
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('grace_banner_dismissed', state);
    setDismissed(true);
  };

  return (
    <div
      className={`${style.bg} border-b-2 ${style.border} px-4 py-3 sm:px-6`}
      role="alert"
      data-testid={`subscription-banner-${state}`}
    >
      <div className="container mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <AlertTriangle className={`w-5 h-5 sm:w-6 sm:h-6 ${style.icon} shrink-0 mt-0.5 sm:mt-0`} aria-hidden />
        <div className="flex-1 min-w-0">
          <p className={`text-sm sm:text-base font-semibold ${style.title}`}>{style.title_text}</p>
          {hint && <p className={`text-xs sm:text-sm ${style.body} mt-0.5`}>{hint}</p>}
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={handleCta}
            className={`${style.button} text-xs sm:text-sm font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors`}
            data-testid={`subscription-banner-cta-${state}`}
          >
            <CreditCard className="w-3.5 h-3.5" />
            {style.cta}
          </button>
          {style.dismissible && (
            <button
              type="button"
              onClick={handleDismiss}
              className={`${style.body} hover:opacity-80 p-1.5 rounded-md transition-opacity`}
              aria-label="Dismiss"
              data-testid="subscription-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGraceBanner;
